/**
 * ``POST billing/estimate`` params built from wizard state, so every flow quotes exactly what it
 * will submit (the backend prices the hold with the same inputs).
 */

import { MAX_TOTAL_RECORDS } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/constants';
import { MAX_RECORD_LIMIT } from '@/features/datasets/components/DataCollection/RetrievalAgent/constants';

/** A positive whole count no larger than *ceiling*, else ``null`` (over the ceiling the form shows its own error). */
const countWithin = (value, ceiling) => {
    if (value === '' || value == null) return null;
    const count = Number(value);
    return Number.isInteger(count) && count > 0 && count <= ceiling ? count : null;
};

/**
 * Generate-with-AI: ``{dataset_size, record_counts?}``.
 *
 * ``recordCounts`` are the per-label (or per-entity) inputs of the records step; records are priced
 * by count alone (text length does not change the price). ``null`` until a positive total within
 * the 10,000 ceiling exists, which keeps the quote off an invalid form.
 */
export function dataGenerationEstimateParams({ totalRecords, recordCounts }) {
    const total = countWithin(totalRecords, MAX_TOTAL_RECORDS);
    if (!total) return null;
    const counts = Object.fromEntries(
        Object.entries(recordCounts || {}).map(([name, count]) => [name, Number.isFinite(count) ? count : 0])
    );
    return {
        dataset_size: total,
        ...(Object.keys(counts).length ? { record_counts: counts } : {}),
    };
}

/** Retrieval agent: ``{record_limit}``; ``null`` while the limit input is empty or over the 100,000 ceiling. */
export function dataRetrievalEstimateParams(recordLimit) {
    const limit = countWithin(recordLimit, MAX_RECORD_LIMIT);
    return limit ? { record_limit: limit } : null;
}
